import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import {
  Badge,
  Box,
  Flex,
  FormProvider,
  ModalProvider,
  Tabs,
  useCheckIfSubscriptionFeatureActiveBroker,
  UpgradeSubscriptionModalType,
  DropdownOptions,
  TooltipPopup,
} from "dash-ui";

import AllPropertiesListContainer from "containers/properties/AllPropertiesListContainer/AllPropertiesListContainer";
import AllAlertsPropertiesContainer from "containers/alerts/AllAlertsPropertiesContainer/AllAlertsPropertiesContainer";
import AddAlertModal from "containers/alerts/AddAlertModal/AddAlertModal";
import { FormFieldsList } from "containers/alerts/AddAlertModal/constants/constants";
import {
  useGetNumberOfNewAlertItemsQuery,
  useObserveBrokerAlertItemChangesSubscription,
} from "gql/generated.graphql";
import UpgradeSubscriptionModal from "common/components/UpgradeSubscriptionModal/UpgradeSubscriptionModal";
import { SubscriptionChangeConfirmationModal } from "pages/ProfilePage/components/ProfileBilling/components";

const StyledBox = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.n30};
`;

enum TabsKeys {
  ALL_LISTING = "ALL_LISTING",
  ALERT_LISTING = "ALERT_LISTING",
}

const PropertiesPage = () => {
  const [activeTab, setActiveTab] = useState<TabsKeys>(TabsKeys.ALL_LISTING);
  const { t } = useTranslation("pages");

  const tabsMap: Record<TabsKeys, () => ReactNode> = {
    [TabsKeys.ALL_LISTING]: () => <AllPropertiesListContainer />,
    [TabsKeys.ALERT_LISTING]: () => <AllAlertsPropertiesContainer />,
  };

  const { data, refetch } = useGetNumberOfNewAlertItemsQuery({
    fetchPolicy: "network-only",
  });

  useObserveBrokerAlertItemChangesSubscription({
    onData() {
      refetch();
    },
  });

  return (
    <ModalProvider>
      <>
        <DropdownOptions />
        <TooltipPopup />
        <Flex alignSelf="stretch" w="100%" flexDirection="column">
          <StyledBox w="100%">
            <Tabs
              variation="secondary"
              tabs={[
                {
                  key: TabsKeys.ALL_LISTING,
                  label: t("propertyPage.allListing") || "",
                },
                {
                  key: TabsKeys.ALERT_LISTING,
                  children: (
                    <>
                      {t("propertyPage.alertListing")}
                      {data?.getNumberOfNewAlertItems ? (
                        <Badge
                          ml={2}
                          title={data?.getNumberOfNewAlertItems.toString()}
                          type="activeSolid"
                        />
                      ) : null}
                    </>
                  ),
                },
              ]}
              activeTab={activeTab}
              onClick={(tab) => setActiveTab(tab as TabsKeys)}
              w="100%"
            />
          </StyledBox>
          <Flex flexDirection="column" flex={1} w="100%">
            {tabsMap[activeTab]() || null}
          </Flex>
        </Flex>
        <FormProvider fieldsList={FormFieldsList}>
          <AddAlertModal />
        </FormProvider>
        <SubscriptionChangeConfirmationModal />
        <UpgradeSubscriptionModal
          type={UpgradeSubscriptionModalType.PORTFOLIO}
        />
      </>
    </ModalProvider>
  );
};

export default PropertiesPage;
